import { Slot, SlotFillProvider, withFilters }              from '@wordpress/components';
import { __ }                                               from '@wordpress/i18n';
import { Formik }                                           from 'formik';
import React                                                from 'react';
import SettingsFormContainerPropsInterface                  from '../../../interfaces/SettingsFormContainerPropsInterface';
import SettingsFormPropsPlanInterface                       from '../../../interfaces/SettingsFormPropsPlanInterface';
import AdminPageSection                                     from '../AdminPageSection';
import CheckboxField                                        from '../Fields/CheckboxField';
import RadioGroupField                                      from '../Fields/RadioGroupField';
import SevereAlert                                          from '../SevereAlert';
import TextField                                            from '../Fields/TextField';
import LockedFieldWrapper                                   from '../LockedFieldWrapper';

interface IntegrationsSettingsInterface {
    google_places_api_key: string;
    turnstile_enabled: boolean;
    turnstile_site_key: string;
    turnstile_secret_key: string;
    turnstile_checkout_enabled: boolean;
    turnstile_order_pay_enabled: boolean;
    turnstile_login_enabled: boolean;
    turnstile_register_enabled: boolean;
    turnstile_position: string;
    turnstile_theme: string;
    turnstile_size: string;
    turnstile_guest_only: boolean;
    turnstile_has_conflict: boolean;
    turnstile_conflict_notice: string;
}

interface IntegrationsSettingsFormPropsInterface extends SettingsFormContainerPropsInterface {
    settings: IntegrationsSettingsInterface;
    integrations: any[];
    plan: SettingsFormPropsPlanInterface;

}

const IntegrationsSettingsForm: React.FC<IntegrationsSettingsFormPropsInterface> = ( containerProps ) => {
    const { saveSettings, isLoading, searchTerm } = containerProps;

    const AdditionalSettings = withFilters(
        'CheckoutWC.Admin.Settings.Pages.Integrations',
    )( () => <></> );

    return (
        <SlotFillProvider>
            <AdditionalSettings />
            <Formik
                initialValues={{
                    ...containerProps.settings,
                    ...containerProps.integrations.reduce( ( acc, integration ) => {
                        acc[ integration.name ] = integration.initial_value;
                        return acc;
                    }, {} ),
                }}
                enableReinitialize={true}
                onSubmit={async ( values ) => {
                    await saveSettings( values );
                }}
            >
                { ( props ) => (
                    <form className={'space-y-6 transition-all'} style={{ filter: isLoading ? 'blur(2px)' : 'none' }} onSubmit={props.handleSubmit}>
                        <AdminPageSection
                            title={__( 'Cloudflare Turnstile', 'checkout-wc' )}
                            description={__( 'Configure Cloudflare Turnstile CAPTCHA protection for your checkout and account pages.', 'checkout-wc' )}
                            content={
                                <>
                                    <LockedFieldWrapper plan={containerProps.plan} slug={'cloudflare-turnstile'} locked={containerProps.plan.plan_level < 3} requiredPlans={containerProps.plan.labels.required_list[ 3 ]}>
                                        <CheckboxField
                                            name="turnstile_enabled"
                                            label={__( 'Enable Cloudflare Turnstile', 'checkout-wc' )}
                                            description={__( 'Enable Cloudflare Turnstile CAPTCHA protection', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.values.turnstile_has_conflict || containerProps.plan.plan_level < 3}
                                        />
                                    </LockedFieldWrapper>

                                    {props.values.turnstile_has_conflict && (
                                        <SevereAlert description={props.values.turnstile_conflict_notice} />
                                    )}

                                    {containerProps.plan.plan_level >= 3 && props.values.turnstile_enabled && !props.values.turnstile_has_conflict && (
                                        <>
                                            <TextField
                                                name="turnstile_site_key"
                                                label={__( 'Site Key', 'checkout-wc' )}
                                                description={__( 'Your Cloudflare Turnstile site key', 'checkout-wc' )}
                                                searchTerm={searchTerm}
                                            />

                                            <TextField
                                                name="turnstile_secret_key"
                                                label={__( 'Secret Key', 'checkout-wc' )}
                                                type="password"
                                                description={__( 'Your Cloudflare Turnstile secret key', 'checkout-wc' )}
                                                searchTerm={searchTerm}
                                            />

                                            <CheckboxField
                                                name="turnstile_checkout_enabled"
                                                label={__( 'Enable on Checkout', 'checkout-wc' )}
                                                description={__( 'Show Turnstile on the checkout page', 'checkout-wc' )}
                                                searchTerm={searchTerm}
                                            />

                                            <CheckboxField
                                                name="turnstile_order_pay_enabled"
                                                label={__( 'Enable on Order Pay', 'checkout-wc' )}
                                                description={__( 'Show Turnstile on the order pay page', 'checkout-wc' )}
                                                searchTerm={searchTerm}
                                            />

                                            <CheckboxField
                                                name="turnstile_login_enabled"
                                                label={__( 'Enable on Login', 'checkout-wc' )}
                                                description={__( 'Show Turnstile on the My Account login form', 'checkout-wc' )}
                                                searchTerm={searchTerm}
                                            />

                                            <CheckboxField
                                                name="turnstile_register_enabled"
                                                label={__( 'Enable on Registration', 'checkout-wc' )}
                                                description={__( 'Show Turnstile on the My Account registration form', 'checkout-wc' )}
                                                searchTerm={searchTerm}
                                            />

                                            <RadioGroupField
                                                name="turnstile_position"
                                                label={__( 'Position on Checkout', 'checkout-wc' )}
                                                description={__( 'Where to display the Turnstile widget on checkout', 'checkout-wc' )}
                                                options={[
                                                    {
                                                        value: 'before_place_order',
                                                        label: __( 'Before Place Order Button', 'checkout-wc' ),
                                                        description: __( 'Display just before the place order button', 'checkout-wc' ),
                                                    },
                                                    {
                                                        value: 'before_payment_methods',
                                                        label: __( 'Before Payment Methods', 'checkout-wc' ),
                                                        description: __( 'Display before payment method selection', 'checkout-wc' ),
                                                    },
                                                    {
                                                        value: 'after_payment_methods',
                                                        label: __( 'After Payment Methods', 'checkout-wc' ),
                                                        description: __( 'Display after payment method selection', 'checkout-wc' ),
                                                    },
                                                ]}
                                                searchTerm={searchTerm}
                                            />

                                            <RadioGroupField
                                                name="turnstile_theme"
                                                label={__( 'Theme', 'checkout-wc' )}
                                                description={__( 'Visual theme for the Turnstile widget', 'checkout-wc' )}
                                                options={[
                                                    { value: 'light', label: __( 'Light', 'checkout-wc' ), description: __( 'Light theme', 'checkout-wc' ) },
                                                    { value: 'dark', label: __( 'Dark', 'checkout-wc' ), description: __( 'Dark theme', 'checkout-wc' ) },
                                                    { value: 'auto', label: __( 'Auto', 'checkout-wc' ), description: __( 'Automatic based on user preference', 'checkout-wc' ) },
                                                ]}
                                                searchTerm={searchTerm}
                                            />

                                            <RadioGroupField
                                                name="turnstile_size"
                                                label={__( 'Size', 'checkout-wc' )}
                                                description={__( 'Size of the Turnstile widget', 'checkout-wc' )}
                                                options={[
                                                    { value: 'normal', label: __( 'Normal', 'checkout-wc' ), description: __( 'Standard size widget', 'checkout-wc' ) },
                                                    { value: 'compact', label: __( 'Compact', 'checkout-wc' ), description: __( 'Smaller widget size', 'checkout-wc' ) },
                                                ]}
                                                searchTerm={searchTerm}
                                            />

                                            <CheckboxField
                                                name="turnstile_guest_only"
                                                label={__( 'Guest Users Only', 'checkout-wc' )}
                                                description={__( 'Only show Turnstile for guest (non-logged-in) users', 'checkout-wc' )}
                                                searchTerm={searchTerm}
                                            />
                                        </>
                                    )}
                                </>
                            }
                        />

                        {containerProps.plan.plan_level > 0 && ( <AdminPageSection
                            title={__( 'Google API', 'checkout-wc' )}
                            description={__( 'Used for the maps embed on the thank you page as well as Google Address Autocomplete.', 'checkout-wc' )}
                            content={
                                <>
                                    <TextField
                                        name="google_places_api_key"
                                        label={__( 'Google API Key', 'checkout-wc' )}
                                        description={__( 'Used by Address Autocomplete and Thank You Page Maps Embed.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <Slot name="CheckoutWC.Admin.Pages.Integrations.Google" />
                                </>
                            }
                        /> )}

                        { containerProps.integrations.length > 0
                            && (
                                <AdminPageSection
                                    title={__( 'Themes and Plugins', 'checkout-wc' )}
                                    description={__( 'Integrations with 3rd party themes and plugins.', 'checkout-wc' )}
                                    content={
                                        <>
                                            {containerProps.integrations.map( ( integration: any ) => (
                                                <CheckboxField
                                                    key={integration.name}
                                                    name={integration.name}
                                                    label={integration.label}
                                                    description={integration.description}
                                                    onBlur={props.handleBlur}
                                                    checked={props.values[ integration.name ]}
                                                    onChange={props.handleChange}
                                                    searchTerm={searchTerm}
                                                />
                                            ) )}

                                            <Slot name="CheckoutWC.Admin.Pages.Integrations.ThirdParty" />
                                        </>
                                    }
                                />
                            )
                        }

                        <button className={'cfw_admin_page_submit hidden'} type="submit">Submit</button>
                    </form>
                )}
            </Formik>
        </SlotFillProvider>
    );
};

export default IntegrationsSettingsForm;
